/**
 * The three planning steps (Sort, Plan my week, Plan my day) as a compact stepper shown above each step's page, and the
 * Planning wizard: started from Today, it walks the steps in order — what to do on each, what is still left, and Next.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowRight, Wand2, X } from "lucide-react";
import { Fragment, useState } from "react";

const KEY = "one-plan-wizard";
const wizardOn = () => { try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; } };
const setWizard = (on: boolean) => { try { if (on) sessionStorage.setItem(KEY, "1"); else sessionStorage.removeItem(KEY); } catch { /* private mode: the wizard just won't persist */ } };

/** The day is planned: the wizard is over. */
export const finishWizard = () => setWizard(false);

/** "Planning wizard" button (on the planning steps): starts at step 1. */
export function WizardButton({ className }: { className?: string }) {
  const navigate = useNavigate();
  return <button type="button" className={className ?? "btn btn-sm btn-primary"} onClick={() => { setWizard(true); void navigate({ to: "/plan/sort" }); }}><Wand2 size={15} /> Planning wizard</button>;
}

const GUIDE: Record<PlanStep, { title: string; text: string; next?: { to: "/plan/week" | "/plan"; label: string } }> = {
  sort: { title: "Step 1 of 3 · Sort", text: "Give every task a when: Today, Tomorrow, This week, Next week, a date, Later or Someday. Use the number keys to go fast; Skip what you can't decide yet.", next: { to: "/plan/week", label: "Next: Plan my week" } },
  week: { title: "Step 2 of 3 · Plan my week", text: "Your meetings and free time are shown for each day. Drag every task on the left onto a day (or press Spread for me, check, Apply). Tasks that already have a day can stay.", next: { to: "/plan", label: "Next: Plan my day" } },
  day: { title: "Step 3 of 3 · Plan my day", text: "Tick what you'll do today, drag tasks to a time on the timeline (or press Fit around my meetings), then save. Turn on Block in Outlook to put the time in your calendar." },
};

/** Shown on each step while the wizard runs. `left` = what still needs doing on this step (Next is offered anyway). */
function WizardBar({ current, left }: { current: PlanStep; left?: number }) {
  const navigate = useNavigate();
  const [, force] = useState(0);
  const g = GUIDE[current];
  const exit = () => { setWizard(false); force((n) => n + 1); };
  return (
    <div className="mt-2 rounded-xl border border-brand-200 bg-brand-50/60 p-3 text-sm">
      <div className="flex items-start gap-2">
        <Wand2 size={16} className="mt-0.5 shrink-0 text-brand-700" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-brand-800">{g.title}</div>
          <p className="mt-0.5">{g.text}</p>
          {left !== undefined && <p className={clsx("mt-1 text-xs font-semibold", left ? "text-amber-700" : "text-emerald-700")}>{left ? `${left} still to do on this step` : "This step is done"}</p>}
        </div>
        <button type="button" className="btn btn-sm" onClick={exit} aria-label="Exit the wizard" title="Exit the wizard"><X size={14} /></button>
      </div>
      <div className="mt-2 flex justify-end gap-2">
        {g.next
          ? <button type="button" className={clsx("btn btn-sm", !left && "btn-primary")} onClick={() => void navigate({ to: g.next!.to })}>{left ? "Skip ahead: " : ""}{g.next.label} <ArrowRight size={14} /></button>
          : <button type="button" className="btn btn-sm" onClick={exit}>Finish the wizard</button>}
      </div>
    </div>
  );
}

export type PlanStep = "sort" | "week" | "day";

const STEPS: Array<{ step: PlanStep; n: number; label: string; to: "/plan/sort" | "/plan/week" | "/plan" }> = [
  { step: "sort", n: 1, label: "Sort", to: "/plan/sort" },
  { step: "week", n: 2, label: "Plan my week", to: "/plan/week" },
  { step: "day", n: 3, label: "Plan my day", to: "/plan" },
];

export function PlanSteps({ current, left }: { current: PlanStep; left?: number }) {
  return (
    <nav aria-label="Planning steps" className="mb-3">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm">
        {STEPS.map((s, i) => {
          const on = s.step === current;
          return (
            <Fragment key={s.step}>
              {i > 0 && <li aria-hidden className="muted px-0.5">·</li>}
              <li>
                <Link
                  to={s.to}
                  aria-current={on ? "step" : undefined}
                  className={clsx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold transition", on ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200" : "muted hover:text-[var(--text)]")}
                >
                  <span className={clsx("grid size-5 place-items-center rounded-full text-[11px] num", on ? "bg-brand-600 text-white" : "bg-[var(--panel-2)] ring-1 ring-[var(--line)]")}>{s.n}</span>
                  {s.label}
                </Link>
              </li>
            </Fragment>
          );
        })}
        {!wizardOn() && <li className="ml-auto"><WizardButton className="btn btn-ghost btn-sm text-brand-700" /></li>}
      </ol>
      {wizardOn() && <WizardBar current={current} left={left} />}
    </nav>
  );
}
